import { useEffect, useState, type ReactNode } from "react";
import { MobileSheet } from "./MobileSheet.tsx";
import { TeamAbbrevLink } from "./TeamAbbrevLink.tsx";
import { MobileEntityRow } from "../mobile/mobilePrimitives.tsx";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { useLocalPartial } from "../util/local.ts";
import { translateTeamFullName } from "../util/teamNames.ts";
import { toWorker } from "../util/toWorker.ts";

// M3-4: the standalone draft-picks page (view "draftPicks", routes
// /l/:lid/draft_picks[/:abbrev]) was retired into this bottom-sheet, opened in
// place from the Draft page, the Trade page and the market hub. It shows the
// page's core content — the opened team's pick assets grouped into what it holds
// (own-original + acquired) and what it owes out — but as an overlay that never
// touches the navigation back stack (see MOBILE_SHEET_CONTRACT_V1 in
// MobileSheet.tsx). The pick roster is fetched ad hoc on open via the
// `getDraftPicksSheet` worker RPC (the M3 data pattern), not the page-view
// pipeline. The old page's team-switcher dropdown is dropped: the sheet always
// serves the tid it was opened for. The footer trade CTA is a NAVIGATE exit — it
// links to the trade page, unmounting the sheet with the underlying view.

type DraftPickAsset = {
  dpid?: number;
  season: number | "fantasy" | "expansion";
  round: number;
  originalTid: number;
  originalAbbrev: string;
  pick: number;
  projectedPick?: number;
};

const seasonLabel = (pick: DraftPickAsset): string => {
  if (pick.season === "fantasy") {
    return i18n.t("draftPicks.fantasy", { ns: "ui", defaultValue: "Fantasy" });
  }
  if (pick.season === "expansion") {
    return i18n.t("draftPicks.expansion", { ns: "ui", defaultValue: "Expansion" });
  }
  return `${pick.season}`;
};

const renderPickNumber = (pick: DraftPickAsset): ReactNode => {
  const pickNumber = pick.pick > 0 ? pick.pick : pick.projectedPick;
  if (pickNumber === undefined) {
    return null;
  }
  const label = i18n.t("draftPicks.p2v2PickNumber", {
    ns: "ui",
    pick: pickNumber,
    defaultValue: "#{{pick}}",
  });
  return pick.pick > 0 ? label : <i className="text-body-secondary">{label}</i>;
};

const PickRows = ({ picks }: { picks: DraftPickAsset[] }) =>
  picks.length > 0 ? (
    <section className="d-grid gap-2" data-draft-picks-sheet-rows="">
      {picks.map((dp) => (
        <MobileEntityRow
          data-draft-picks-sheet-row=""
          detail={
            <>
              <TeamAbbrevLink tid={dp.originalTid} abbrev={dp.originalAbbrev} />
              {renderPickNumber(dp) ? <> · {renderPickNumber(dp)}</> : null}
            </>
          }
          key={dp.dpid}
          meta={i18n.t("draftPicks.roundPick", {
            ns: "ui",
            round: dp.round,
            defaultValue: "Round {{round}} pick",
          })}
          title={seasonLabel(dp)}
        />
      ))}
    </section>
  ) : (
    <p className="text-body-secondary mb-0">
      {i18n.t("common.none", { ns: "ui", defaultValue: "None" })}
    </p>
  );

export const DraftPicksSheet = ({
  onClose,
  open,
  tid,
}: {
  onClose: () => void;
  open: boolean;
  tid: number;
}) => {
  const { teamInfoCache, userTid } = useLocalPartial(["teamInfoCache", "userTid"]);
  const [data, setData] = useState<
    | {
        draftPicks: DraftPickAsset[];
        draftPicksOutgoing: DraftPickAsset[];
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setData(undefined);
    (async () => {
      const result = await toWorker("main", "getDraftPicksSheet", tid);
      if (!cancelled) {
        setData({
          draftPicks: result.draftPicks,
          draftPicksOutgoing: result.draftPicksOutgoing,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, tid]);

  const teamInfo = teamInfoCache[tid];
  const teamDisplayName = teamInfo
    ? translateTeamFullName(`${teamInfo.region} ${teamInfo.name}`)
    : undefined;
  const title =
    tid !== userTid && teamDisplayName !== undefined
      ? i18n.t("draftPicks.otherTeamTitle", {
          ns: "ui",
          team: teamDisplayName,
          defaultValue: "{{team}} draft picks",
        })
      : i18n.t("draftPicks.title", { ns: "ui", defaultValue: "My Draft Picks" });

  const footer = (
    <a
      className="btn btn-primary w-100"
      data-draft-picks-sheet-trade-cta=""
      href={helpers.leagueUrl(["trade"])}
    >
      {i18n.t("draftPicks.p1s08OpenTrade", {
        ns: "ui",
        defaultValue: "Open trade board",
      })}
    </a>
  );

  return (
    <MobileSheet footer={footer} onClose={onClose} open={open} title={title}>
      {data === undefined ? (
        <div className="d-flex justify-content-center py-4" data-draft-picks-sheet-loading="">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">
              {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
            </span>
          </div>
        </div>
      ) : (
        <div data-draft-picks-sheet-view="">
          <h2 className="h6" data-draft-picks-sheet-owned="">
            {i18n.t("draftPicks.ownedPicks", { ns: "ui", defaultValue: "Owned picks" })}
          </h2>
          <PickRows picks={data.draftPicks} />
          <h2 className="h6 mt-3" data-draft-picks-sheet-outgoing="">
            {i18n.t("draftPicks.outgoingPicks", { ns: "ui", defaultValue: "Outgoing picks" })}
          </h2>
          <PickRows picks={data.draftPicksOutgoing} />
        </div>
      )}
    </MobileSheet>
  );
};

export default DraftPicksSheet;
